import { useNavigate } from "react-router";
import { Link2, ChevronRight } from "lucide-react";
import type { KnowledgeChainLink } from "../types/social";

// PRD §4.5 — Knowledge chain sidebar section.
// Renders the full ordered chain: root → first borrower → … → current author.
// Each author entry links to their profile.
// Public destinations are clickable; private ones render as (private), not clickable.
// The chain itself is NEVER hidden — visitors always see a link exists.

interface Props {
  chain: KnowledgeChainLink[]; // root first, current card last
  loading?: boolean;
}

export function KnowledgeChainSidebar({ chain, loading }: Props) {
  const navigate = useNavigate();

  if (loading) {
    return (
      <section className="rounded-2xl bg-card/40 border border-border/30 p-4">
        <Header />
        <div className="mt-3 text-muted-foreground/60" style={{ fontSize: "0.82rem" }}>
          Loading chain…
        </div>
      </section>
    );
  }

  // Root card (length <= 1): still render the section so the sidebar's structure
  // is consistent — just show "Original — start of the chain" rather than disappearing.
  if (chain.length <= 1) {
    const only = chain[0];
    const isAnonymous = !only || only.author.anonymous || !only.author.handle;
    return (
      <section className="rounded-2xl bg-card/40 border border-border/30 p-4">
        <Header />
        <div className="mt-3 flex items-start gap-2">
          <div className="flex flex-col items-center pt-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-accent/60" />
          </div>
          <div className="flex-1 min-w-0">
            {only && !isAnonymous ? (
              <button
                onClick={() => navigate(`/u/${only.author.handle}`)}
                className="text-foreground hover:text-accent transition-colors"
                style={{ fontSize: "0.86rem" }}
              >
                @{only.author.handle}
              </button>
            ) : (
              <span
                className="text-muted-foreground/80 italic"
                style={{ fontSize: "0.86rem", fontFamily: "'Fraunces', Georgia, serif" }}
              >
                {only ? "Anonymous" : "Original"}
              </span>
            )}
            <div className="mt-0.5 text-muted-foreground/70" style={{ fontSize: "0.74rem" }}>
              Start of the chain — nothing borrowed from yet.
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="rounded-2xl bg-card/40 border border-border/30 p-4">
      <Header />
      <ol className="mt-3 space-y-2">
        {chain.map((link, i) => {
          const isLast = i === chain.length - 1;
          const isAnonymous = link.author.anonymous || !link.author.handle;
          return (
            <li key={link.card_id} className="flex items-start gap-2">
              <div className="flex flex-col items-center pt-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-accent/60" />
                {!isLast && <span className="w-px flex-1 bg-border/40 mt-0.5" style={{ minHeight: 18 }} />}
              </div>
              <div className="flex-1 min-w-0">
                {isAnonymous ? (
                  <span
                    className="text-muted-foreground/80 italic"
                    style={{ fontSize: "0.86rem", fontFamily: "'Fraunces', Georgia, serif" }}
                    title="Anonymous — author connection broken"
                  >
                    Anonymous
                  </span>
                ) : (
                  <button
                    onClick={() => navigate(`/u/${link.author.handle}`)}
                    className="text-foreground hover:text-accent transition-colors"
                    style={{ fontSize: "0.86rem" }}
                  >
                    @{link.author.handle}
                  </button>
                )}
                {link.destination_work && (
                  <div className="mt-0.5 flex items-center gap-1 text-muted-foreground" style={{ fontSize: "0.76rem" }}>
                    <ChevronRight size={11} className="opacity-50" />
                    {link.destination_work.kind === "private" ? (
                      <span className="italic">(private)</span>
                    ) : (
                      <button
                        onClick={() => {
                          const d = link.destination_work;
                          if (!d || d.kind === "private") return;
                          if (d.kind === "edition") navigate(`/editions/${d.id}`);
                          else if (d.kind === "collection") navigate(`/collections/${d.id}`);
                        }}
                        className="hover:text-foreground transition-colors line-clamp-1 text-left"
                      >
                        {link.destination_work.kind === "card"
                          ? "loose card"
                          : link.destination_work.title}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

function Header() {
  return (
    <div className="flex items-center gap-2 text-muted-foreground">
      <Link2 size={14} className="text-accent/70" />
      <span style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: "0.92rem" }}>
        Knowledge chain
      </span>
    </div>
  );
}
